import { Receipt, FileText, PencilLine, ArrowDownLeft, ArrowUpRight, House, Building2, UserRound } from "lucide-react";

/** Regnskap – felles konstanter for kontoplaner, kilder, posttyper og måneds-hjelpere. */
export const HOUSEHOLD_HOME = "/regnskap";

/** Kontoplaner som kan velges per virksomhet (speiler backend CHART_PLANS). */
export const CHART_PLANS = {
  husholdning: { label: "Husholdningsregnskap", short: "Husholdning", icon: House, description: "COICOP-hovedgrupper (SSB) med SIFO-inspirerte undergrupper. For privatpersoner og husholdninger som ikke er registrert i Brønnøysundregistrene." },
  ns4102_as: { label: "NS 4102 – aksjeselskap", short: "NS 4102 (AS)", icon: Building2, description: "Norsk Standard kontoplan for små og mellomstore AS: salgsinntekter, varekostnad, lønn, driftskostnader, finans og skatt." },
  ns4102_enk: { label: "NS 4102 – personlig foretak", short: "NS 4102 (ENK)", icon: UserRound, description: "Norsk Standard kontoplan tilpasset enkeltpersonforetak og ANS/DA, med privatuttak og forskuddsskatt som egne konti." },
};

/** FORESLÅTT kontoplan ut fra virksomhetstype og org.nr (speiler backend suggested_chart_plan). Kun et forslag i skjemaer – eksisterende virksomheter får aldri plan automatisk. */
export const suggestedChartPlan = (entityType, orgNumber) => {
  const digits = (orgNumber || "").replace(/\D/g, "");
  const personal = ["enk", "ans", "da"].includes(entityType);
  if (!digits) return (entityType || "privat") === "privat" ? "husholdning" : personal ? "ns4102_enk" : "ns4102_as";
  return personal ? "ns4102_enk" : "ns4102_as";
};
export const defaultChartPlan = suggestedChartPlan;

/** Terminologi for aktiv plan (fra /household/plan → standard), med trygge standardverdier. */
export const planTerms = (standard) => ({
  key: standard?.key || "husholdning",
  eyebrow: standard?.eyebrow || "Husholdning",
  short: standard?.short || "Husholdning",
  isHousehold: (standard?.key || "husholdning") === "husholdning",
  group: standard?.group_label || "hovedgruppe",
  category: standard?.category_label || "undergruppe",
  income: standard?.income_label || "inntektskategori",
  expenses: standard?.expense_label || "Utgifter",
  outside: standard?.outside_label || "utenfor COICOP",
  planName: standard?.name || "Husholdningsregnskap",
  description: standard?.description || "",
  regnskapOrd: (standard?.key || "husholdning") === "husholdning" ? "husholdningsregnskapet" : "regnskapet",
});

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
export { cap as capitalize };
export const HOUSEHOLD_SOURCES = {
  regning: { label: "Regning", short: "Regning", icon: FileText, cls: "status-til_betaling", help: "Fra Regninger-modulen, telles i forfallsmåneden. Uten kategori telles den som «Ukategorisert»." },
  faktura: { label: "Faktura", short: "Faktura", icon: Receipt, cls: "status-avtalegiro", help: "Fra Faktura-modulen, telles i utstedelsesmåneden når du har valgt inntektskategori. Kreditnota trekkes fra." },
  manuell: { label: "Manuell post", short: "Manuell", icon: PencilLine, cls: "status-ma_kontrolleres", help: "Ført direkte i regnskapet." },
};

export const HOUSEHOLD_KINDS = {
  utgift: { label: "Utgift", icon: ArrowUpRight, tone: "text-flag" },
  inntekt: { label: "Inntekt", icon: ArrowDownLeft, tone: "text-success" },
};

export const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${String(Math.floor(idx / 12)).padStart(4, "0")}-${String((idx % 12) + 1).padStart(2, "0")}`;
};

export const isValidMonth = (ym) => /^\d{4}-(0[1-9]|1[0-2])$/.test(ym || "");

/** Flat liste av kategorier fra plan (grupper + undergrupper + inntekt). */
export const flattenPlan = (plan) => {
  if (!plan) return [];
  const out = [];
  for (const g of plan.groups || []) {
    out.push({ ...g, children: undefined, depth: 0 });
    for (const c of g.children || []) out.push({ ...c, depth: 1, group: g });
  }
  for (const c of plan.income || []) out.push({ ...c, depth: 0 });
  return out;
};

/** Prosent av budsjett (0–100+), null når budsjett mangler. */
export const budgetPct = (total, budget) => (budget ? Math.round((Number(total || 0) / Number(budget)) * 100) : null);
